import { useState, useEffect } from 'react';
import { api, eventShareApi, contributionApi } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import type { Member, OwnerContributionRequestView, ContributionAsk } from '../types';
import { ContributionAskFields, emptyContributionAsk, hasAnyAsk } from './ContributionAskFields';

interface Props {
  eventId: string;
  eventTitle: string;
  onClose: () => void;
  /** 'ask' opens straight into the curated-collaboration ask flow, filtered to
   * only people who can actually be asked — the entry point for a dedicated
   * "Ask to contribute" action on the event page, distinct from general access
   * management. Either mode can switch to the other without closing the modal. */
  initialMode?: 'share' | 'ask';
}

/**
 * The home for THIS event's access: who can see/edit it, and — for full-access
 * people — an optional "ask for help" action, plus a fulfillment list for any
 * open asks. Adding brand-new people to My Circle stays a Members.tsx-only
 * action; this modal only adjusts access/asks for people already in the circle.
 */
export function ShareEventModal({ eventId, eventTitle, onClose, initialMode = 'share' }: Props) {
  const { user } = useAuth();
  const [mode, setMode] = useState<'share' | 'ask'>(initialMode);
  const [members, setMembers] = useState<Member[]>([]);
  const [sharedWith, setSharedWith] = useState<Set<string>>(new Set());
  const [requests, setRequests] = useState<OwnerContributionRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [askMemberId, setAskMemberId] = useState<string | null>(null);
  const [askValue, setAskValue] = useState<ContributionAsk>(emptyContributionAsk());
  const [askSending, setAskSending] = useState(false);
  const [askError, setAskError] = useState('');

  function loadRequests() {
    contributionApi.listForEvent(eventId).then(setRequests).catch(() => {});
  }

  useEffect(() => {
    Promise.all([
      api.get<Member[]>('/account/members').then(r => Array.isArray(r.data) ? r.data : []),
      eventShareApi.getShares(eventId).then(data => Array.isArray(data) ? data : []),
    ]).then(([m, shares]) => {
      setMembers(m);
      setSharedWith(new Set(shares.map(s => s.memberId)));
    }).catch(() => {}).finally(() => setLoading(false));
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  async function toggle(memberId: string, currentlyShared: boolean) {
    setSaving(memberId);
    try {
      if (currentlyShared) {
        await eventShareApi.unshare(eventId, memberId);
        setSharedWith(prev => { const n = new Set(prev); n.delete(memberId); return n; });
      } else {
        await eventShareApi.share(eventId, [memberId]);
        setSharedWith(prev => new Set([...prev, memberId]));
      }
    } finally {
      setSaving(null);
    }
  }

  async function handleSendAsk(memberId: string) {
    if (!hasAnyAsk(askValue)) return;
    setAskSending(true);
    setAskError('');
    try {
      await contributionApi.create({ eventIds: [eventId], memberIds: [memberId], ...askValue });
      setAskMemberId(null);
      setAskValue(emptyContributionAsk());
      loadRequests();
    } catch (err: unknown) {
      setAskError((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to send request');
    } finally {
      setAskSending(false);
    }
  }

  const circleMembers = members.filter(m => !m.petType && m.status !== 'no_email' && m.userId !== user?.id);
  const askableIds = new Set(
    circleMembers.filter(m => m.role === 'member' && (m.scope === 'all' || sharedWith.has(m.id))).map(m => m.id)
  );
  const visibleMembers = mode === 'ask' ? circleMembers.filter(m => askableIds.has(m.id)) : circleMembers;

  useEffect(() => {
    // In ask mode, jump straight to the form when there's exactly one person
    // to ask — no need to make them click twice for the common case.
    if (mode === 'ask' && !loading && visibleMembers.length === 1 && askMemberId === null) {
      setAskMemberId(visibleMembers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, loading, visibleMembers.length]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <p className="text-sm font-bold text-slate-900">{mode === 'ask' ? 'Ask to Contribute' : 'Share Event'}</p>
            <p className="text-xs text-slate-400 truncate max-w-[220px]">{eventTitle}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {mode === 'ask' && (
            <button
              type="button"
              onClick={() => setMode('share')}
              className="text-xs font-medium text-indigo-600 hover:underline mb-3"
            >
              ← Manage access instead
            </button>
          )}
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
          ) : visibleMembers.length === 0 ? (
            <div className="text-center py-4">
              {mode === 'ask' ? (
                <>
                  <p className="text-sm text-slate-500">No one has full access to this event yet.</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Give someone full access first, then you can ask them to help.{' '}
                    <button type="button" onClick={() => setMode('share')} className="text-indigo-600 hover:underline">Manage access</button>
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-500">No one in your circle yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Add people from My Circle first.</p>
                </>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {visibleMembers.map(member => {
                const shared = sharedWith.has(member.id);
                const busy = saving === member.id;
                const isAll = member.scope === 'all';
                const canAsk = member.role === 'member' && (isAll || shared);
                const badge = isAll
                  ? (member.role === 'member' ? 'Full access · all events' : 'View only · all events')
                  : (member.role === 'member' ? 'Full access · specific' : 'View only · specific');

                return (
                  <li key={member.id}>
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${shared || isAll ? 'bg-indigo-50' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                        shared || isAll ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        {(member.user.name?.[0] ?? '?').toUpperCase()}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{member.user.name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{badge}</p>
                      </div>
                      {isAll ? (
                        <span className="text-[10px] font-semibold text-indigo-500 flex-shrink-0">Included</span>
                      ) : (
                        <button
                          onClick={() => toggle(member.id, shared)}
                          disabled={busy}
                          className="flex-shrink-0"
                          title={shared ? 'Shared — tap to remove' : 'Not shared — tap to add'}
                        >
                          {busy ? (
                            <div className="w-5 h-5 rounded-full border-2 border-indigo-300 border-t-transparent animate-spin" />
                          ) : shared ? (
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                            </svg>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
                          )}
                        </button>
                      )}
                      {canAsk && (
                        <button
                          onClick={() => { setAskMemberId(v => v === member.id ? null : member.id); setAskValue(emptyContributionAsk()); setAskError(''); }}
                          className={`text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors flex-shrink-0 ${
                            askMemberId === member.id ? 'bg-indigo-600 text-white border-indigo-600' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'
                          }`}
                        >
                          Ask
                        </button>
                      )}
                    </div>
                    {askMemberId === member.id && (
                      <div className="mt-1.5 space-y-2">
                        <ContributionAskFields value={askValue} onChange={setAskValue} />
                        {askError && <p className="text-xs text-red-600">{askError}</p>}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSendAsk(member.id)}
                            disabled={askSending || !hasAnyAsk(askValue)}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                          >
                            {askSending ? 'Sending…' : 'Send request'}
                          </button>
                          <button onClick={() => setAskMemberId(null)} className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-xs hover:bg-slate-50 transition-colors">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {requests.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Help requested</p>
              <ul className="space-y-1.5">
                {requests.map(r => (
                  <li key={r.id} className={`text-xs ${r.accessOrphaned ? 'text-slate-300' : 'text-slate-600'}`}>
                    <span className="font-medium">{r.member.user.name}</span>
                    {r.fulfillment.photos && <> · {r.fulfillment.photos.added} of {r.fulfillment.photos.requested} photos</>}
                    {r.fulfillment.voice && <> · voice {r.fulfillment.voice.complete ? '✓' : 'not yet'}</>}
                    {r.fulfillment.context && <> · context {r.fulfillment.context.complete ? '✓' : 'not yet'}</>}
                    {r.accessOrphaned && <span className="italic"> — access changed, no longer active</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <p className="text-xs text-slate-400 text-center">
            {mode === 'ask'
              ? 'Only full-access people can be asked — pick someone above and choose what to ask for.'
              : 'Full-access people with "all events" already see this. Toggle specific-events people, or ask a full-access person for help.'}
          </p>
        </div>
      </div>
    </div>
  );
}
